// @ts-strict-ignore
import noop from 'lodash/noop';
import reverseTransformJsonToLegacyRepresentation from 'flow/store/engine/transformation/reverse';
import type {
  SchemaInstance,
  WorkflowProps,
  ServicesByName,
  Action,
  LegacyAction,
  LegacyActionOption,
} from 'flow/store/engine/types';
import type { Services } from '~/flow/types';
import {
  getChangeData,
  makeFetchWithWorkflowInfo,
} from '~/flow/what-if/utils';
import type { SetWhatIfDisabledReasons } from '~/flow/TestingSuite/pages/SelectDataType/utils';
import { routes } from '~/helpers/routes';
import type { InitialAndUnsavedWorkflowBuilderData, HandleCreateWorkflowVersion } from './types';
import { WHAT_IF_ELIGIBLE_WORKFLOW_TYPES } from './constants';
import type { TSuiteBufferItem } from '../changelog/formatters/testingSuiteFormatter/types';

export function getIsChampionChallengerEnabled(opts: {
  championChallengerStarted: string;
  championChallengerEnded: string;
}): boolean {
  if (!opts) {
    return false;
  }
  const {
    championChallengerEnded = null,
    championChallengerStarted = null,
  } = opts;
  return (
    championChallengerStarted && (championChallengerEnded == null)
  );
}


/**
 * Patches legacy input and output published attributes and adds all
 * action options a regular input/output attribute would have.
 */
function patchLegacyInputOutputPAsWithActionOptions({
  actions,
  legacyActions,
  legacyActionOptions,
}: {
  legacyActions: LegacyAction[];
  legacyActionOptions: LegacyActionOption[];
  actions: Record<string, Action>;
}): LegacyAction[] {
  // we find the highest action option id and use it as out baseline
  let nextActionOptionId = Math.max(...legacyActionOptions.map(ao => ao.id)) + 1;
  const patchedLegacyActions = legacyActions.map(legacyAction => {
    if (legacyAction.type !== 'input' && legacyAction.type !== 'output') {
      return legacyAction;
    }

    if (!legacyAction.publishedAttributeToken) {
      return legacyAction;
    }

    const action = actions[legacyAction.publicId];
    const actionId = legacyAction.id;
    const transformedActionOptionIndexNumber = nextActionOptionId;


    /**
     * The rest of this function is brought in almost verbatim from the reverse transformation
     */
    const actionOptionBaseData = {
      actionId,
      id: 0,
      jsonValue: null,
      key: '',
      publishedAttributeToken: action.input?.publishedAttributeToken
        || action.output?.publishedAttributeToken || null,
      publicId: action.input?.actionOptionPublicId || action.output?.actionOptionPublicId,
      value: '',
    };


    let actionOptions: Record<string, typeof actionOptionBaseData>;
    if (legacyAction.type === 'input') {
      actionOptions = {
        data_type: {
          ...actionOptionBaseData,
          id: transformedActionOptionIndexNumber + 2,
          key: 'data_type',
          value: action.input.dataType,
        },
        name: {
          ...actionOptionBaseData,
          id: transformedActionOptionIndexNumber + 3,
          key: 'name',
          value: action.input.name,
        },
        use_jq: {
          ...actionOptionBaseData,
          id: transformedActionOptionIndexNumber + 4,
          key: 'use_jq',
          value: action.input.useJq,
        },
        jq_function: {
          ...actionOptionBaseData,
          id: transformedActionOptionIndexNumber + 5,
          key: 'jq_function',
          value: action.input.jqFunction ?? '',
        },
      };
    } else {
      actionOptions = {
        use_jq: {
          ...actionOptionBaseData,
          id: transformedActionOptionIndexNumber + 1,
          key: 'use_jq',
          value: action.output.useJq || 'false',
          jsonValue: null,
        },
        jq_function: {
          ...actionOptionBaseData,
          id: transformedActionOptionIndexNumber + 2,
          key: 'jq_function',
          value: action.output.jqFunction ?? 'false',
          jsonValue: null,
        },
        expression: {
          ...actionOptionBaseData,
          id: transformedActionOptionIndexNumber + 3,
          key: 'expression',
          value: action.output.expression
            ? JSON.stringify(action.output.expression)
            : '',
          jsonValue: action.output.expression,
        },
        name: {
          ...actionOptionBaseData,
          id: transformedActionOptionIndexNumber + 4,
          key: 'name',
          value: action.output.name,
        },
        data_type: {
          ...actionOptionBaseData,
          id: transformedActionOptionIndexNumber + 5,
          key: 'data_type',
          value: action.output.dataType,
        },
      };
    }


    const countOfCreatedActionOptions = Object.keys(actionOptions).length;
    nextActionOptionId += countOfCreatedActionOptions;

    return {
      ...legacyAction,
      options: actionOptions,
    };
  });

  return patchedLegacyActions;
}

function getReverseTransformJsonToLegacyRepresentation(
  workflowData: Partial<SchemaInstance>,
  workflowProps: WorkflowProps,
) {
  const reversed = reverseTransformJsonToLegacyRepresentation({
    actions: workflowData.actions,
    outcomes: workflowProps.outcomes,
    tags: workflowProps.tags,
    thresholds: workflowData.thresholds,
    dataRoutes: workflowData.dataRoutes,
    services: workflowProps.services,
    sdkConfig: workflowData.sdkConfig,
  });


  /**
   * The legacy editor keeps action options on PAs around until the moment the version is saved.
   * The reverse transformation removes these action options as it should, but some parts of the
   * testing suite/legacy editor (the BooleanRuleRenderer in particular) do expect the action
   * options in PAs. The simplest way to get around the issue without modifying legacy core logic,
   * or without modifying the reverse transformation logic to account for this edge case, is to
   * add back the action options on PAs for the testing suite when opened from the WF2.0.
   *
   * While passing a flag into the reverse transformation function would be much simpler, polluting
   * that business logic is not necessary and it would open a can of worms, so we deal with the issue
   * here.
   */
  const patchedLegacyActions = patchLegacyInputOutputPAsWithActionOptions({
    actions: workflowData.actions,
    legacyActionOptions: reversed.actionOptions,
    legacyActions: reversed.actions,
  });

  return {
    ...reversed,
    actions: patchedLegacyActions,
  };
}


/**
  * This function takes redux workflowProps' services and returns them in API format,
  * taking an object with key of service name and returning an object with key of service id.
 * */
export function formatServicesForApi(
  workflowPropsServices: ServicesByName,
): Services {
  const services = {};
  Object.values(workflowPropsServices).forEach(value => {
    services[value.service_id] = value;
  });
  return services;
}


/**
 * This function takes redux workflowData and workflowProps, reverse transforms it,
 * and returns FlowState compatible data.
 */
export function formatToLegacyFlowState(
  workflowData: Partial<SchemaInstance>,
  workflowProps: WorkflowProps,
) {
  const transformedWorkflowData = getReverseTransformJsonToLegacyRepresentation(
    workflowData,
    workflowProps,
  );

  const {
    reasonCodeGroups, services, tags, outcomes,
  } = workflowProps;

  return {
    ...transformedWorkflowData,
    reasonCodeGroups,
    recurringWorkflows: {}, // not yet in 2.0 redux workflowProps
    relationships: [], // not yet in 2.0
    services,
    tags,
    outcomes,
  };
}


export function getReverseTransformedChangeData(
  initialAndUnsavedWorkflowBuilderData: InitialAndUnsavedWorkflowBuilderData,
) {
  const {
    initialWorkflowData, initialWorkflowProps, unsavedWorkflowData, unsavedWorkflowProps,
  } = initialAndUnsavedWorkflowBuilderData;

  if (initialWorkflowData === null
    || initialWorkflowProps === null
    || unsavedWorkflowData === null
    || unsavedWorkflowProps === null) return [];

  const {
    reasonCodeGroups, services: propServices, tags, outcomes,
  } = unsavedWorkflowProps;
  const services = formatServicesForApi(propServices);

  const {
    actions, actionOptions, thresholds, dataRoutes, matrixModels, sdkConfig,
  } = getReverseTransformJsonToLegacyRepresentation(
    unsavedWorkflowData,
    unsavedWorkflowProps,
  );

  return getChangeData({
    actions,
    actionOptions,
    dataRoutes,
    initialFlowState: formatToLegacyFlowState(initialWorkflowData, initialWorkflowProps),
    reasonCodeGroups,
    recurringWorkflows: {}, // not yet in 2.0 redux workflowProps
    relationships: [], // not yet in 2.0
    sdkConfig,
    services,
    tags,
    thresholds,
    matrixModels,
    outcomes,
  });
}


export function getTestingSuiteCreateWorkflowVersion({
  initAndUnsavedBuilderData, parentVersion, addSnackBar, typhonDomain, workflowToken,
}: {
  initAndUnsavedBuilderData: {
    initialWorkflowData: Partial<SchemaInstance>;
    initialWorkflowProps: WorkflowProps;
    unsavedWorkflowData: Partial<SchemaInstance>;
    unsavedWorkflowProps: WorkflowProps;
  };
  parentVersion: number | null;
  addSnackBar: any;
  typhonDomain: string;
  workflowToken: string;
}): HandleCreateWorkflowVersion {
  const {
    initialWorkflowData, initialWorkflowProps, unsavedWorkflowData, unsavedWorkflowProps,
  } = initAndUnsavedBuilderData;

  if (initialWorkflowData === null
    || initialWorkflowProps === null
    || unsavedWorkflowData === null
    || unsavedWorkflowProps === null) return noop as HandleCreateWorkflowVersion;

  const url = routes.applications.versions.create(workflowToken);
  const {
    reasonCodeGroups, services: propServices,
  } = unsavedWorkflowProps;
  const services = formatServicesForApi(propServices);

  const handleCreateWorkflowVersion = async (extraWorkflowConfig = {}) => {
    try {
      const data = await makeFetchWithWorkflowInfo({
        deps: {
          applicationToken: workflowToken,
          reasonCodeGroups,
          recurringWorkflows: {},
          services,
          typhonDomain,
        },
        url,
        // @ts-ignore: due to FlowState, resolve once legacy workflow editor is removed
        initialFlowState: formatToLegacyFlowState(initialWorkflowData, initialWorkflowProps),
        updatedFlowState: {
          ...formatToLegacyFlowState(unsavedWorkflowData, unsavedWorkflowProps),
          // @ts-ignore: due to FlowState, resolve once legacy workflow editor is removed
          version: {
            public_version: unsavedWorkflowData.publicVersion || null,
          },
        },
        extraPostData: {
          parent_version: parentVersion,
          ...extraWorkflowConfig,
        },
      });

      if (data.message !== 'success') {
        throw new Error(
          data.response?.error?.message
          || 'The network request was not deemed a success',
        );
      }

      addSnackBar({
        duration: 1000,
        title: 'A new version is saved',
        type: 'success',
      });

      // Return the data for subsequent chaining
      return data;
    } catch (err) {
      addSnackBar({
        title: err.message || 'Something went wrong. Please try again or contact support.',
        type: 'danger',
      });
      return err;
    }
  };

  return handleCreateWorkflowVersion;
}


export function shouldDisableWhatIf(args: {
  // todo BUILD-1598 - use a type that doesn't expose the buffer's internals
  changes: Record<string, TSuiteBufferItem>;
  isHistoricalDataAvailable?: { baseVersion?: boolean; otherVersions?: boolean };
  workflowType: string;
  setWhatIfDisabledReasons: SetWhatIfDisabledReasons;
}) {
  const {
    changes, isHistoricalDataAvailable, workflowType, setWhatIfDisabledReasons,
  } = args;
  const {
    setIsWhatIfDisabledByWorkflowType,
    setIsWhatIfDisabledByChanges,
    setIsWhatIfDisabledByBaseData,
    setIsWhatIfDisabledByOtherData,
  } = setWhatIfDisabledReasons;

  // What if can only be run for a subset of workflow types
  const isWorkflowTypeEligible = WHAT_IF_ELIGIBLE_WORKFLOW_TYPES.includes(workflowType);
  if (!isWorkflowTypeEligible) {
    setIsWhatIfDisabledByWorkflowType(true);
    return true;
  }

  const hasChanges = Object.values(changes).length > 0;
  if (!hasChanges) {
    setIsWhatIfDisabledByOtherData(!isHistoricalDataAvailable?.otherVersions);
    return !isHistoricalDataAvailable?.otherVersions;
  }

  const hasIneligibleChanges = Object.values(changes)
    .some(changesForAction => changesForAction.containsIneligible);
  setIsWhatIfDisabledByChanges(hasIneligibleChanges);
  setIsWhatIfDisabledByBaseData(!isHistoricalDataAvailable?.baseVersion);
  return hasIneligibleChanges || !isHistoricalDataAvailable?.baseVersion;
}


export function doesWorkflowTypeSupportTestingSuite(
  workflowType: WorkflowProps['workflow']['type'],
) {
  return workflowType !== 'router';
}
